import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowUpDown, Download, ExternalLink, Loader2, Pencil, Plus, RefreshCw, Search, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  adminInputClass,
  filterPillClass,
  filterPillActiveClass,
  StatusPill,
  TableSkeleton,
} from '@/components/admin/AdminTableStyles';
import AccountingShell, {
  AccountingEmpty,
  AccountingError,
} from '@/components/admin/accounting/AccountingShell';
import ConfirmDeleteDialog from '@/components/admin/accounting/ConfirmDeleteDialog';
import BulkDeleteDialog, { BulkDeleteBar } from '@/components/admin/accounting/BulkDeleteDialog';
import useRowSelection from '@/hooks/useRowSelection';
import {
  accountingError,
  filtersFromSearch,
  searchFromFilters,
} from '@/lib/accounting';
import {
  accountingCustomersExportUrl,
  createAccountingCustomer,
  deleteAccountingCustomer,
  getAccountingCustomers,
  updateAccountingCustomer,
} from '@/lib/api';
import { getToken } from '@/lib/auth';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const DEFAULT_FILTERS = {
  q: '',
  source: 'all',
  sort: 'name',
  direction: 'asc',
  page: '1',
};

const SOURCE_TABS = [
  { id: 'all', label: 'All' },
  { id: 'student', label: 'Students' },
  { id: 'application', label: 'Applicants' },
  { id: 'manual', label: 'Manual' },
];

const SOURCE_TONES = { student: 'emerald', application: 'gold', manual: 'neutral', unknown: 'slate' };

function emptyCustomerForm() {
  return {
    name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    phone_alt: '',
    account_number: '',
    website: '',
    additional_contacts: [],
  };
}

export default function AccountingCustomers() {
  const { profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [filters, setFilters] = useState(() => filtersFromSearch(location.search, DEFAULT_FILTERS));
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [pageInfo, setPageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(emptyCustomerForm());
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleteFor, setDeleteFor] = useState(null);
  const [busyRef, setBusyRef] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);

  // Students and applicants are portal records Accounting only reads. Only a
  // manual customer — one that exists because somebody created it here — can
  // be ticked.
  const selection = useRowSelection(rows, {
    keyOf: (row) => row.ref,
    canSelect: (row) => row.editable,
  });

  useEffect(() => {
    const search = searchFromFilters(filters, DEFAULT_FILTERS);
    if (search !== location.search) {
      navigate({ pathname: location.pathname, search }, { replace: true });
    }
  }, [filters, location.pathname, location.search, navigate]);

  const load = useCallback(
    async ({ quiet } = {}) => {
      if (quiet) setRefreshing(true);
      else setLoading(true);
      setError('');
      try {
        const { data } = await getAccountingCustomers({
          q: filters.q || undefined,
          source: filters.source,
          sort: filters.sort,
          direction: filters.direction,
          page: Number(filters.page) || 1,
        });
        setRows(data?.items || []);
        setCounts(data?.counts || {});
        setPageInfo(data?.page_info || null);
      } catch (err) {
        setError(accountingError(err, 'Customers could not be loaded.'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (patch) => setFilters((current) => ({ ...current, ...patch, page: '1' }));

  const toggleSort = (column) =>
    setFilters((current) => ({
      ...current,
      sort: column,
      direction: current.sort === column && current.direction === 'asc' ? 'desc' : 'asc',
      page: '1',
    }));

  const openCreate = () => {
    setForm(emptyCustomerForm());
    setDialog({ mode: 'create' });
  };

  const openEdit = (customer) => {
    setForm({
      name: customer.name || '',
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      phone_alt: customer.phone_alt || '',
      account_number: customer.account_number || '',
      website: customer.website || '',
      additional_contacts: customer.additional_contacts || [],
    });
    setDialog({ mode: 'edit', customer });
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error('A customer needs a name.');
      return;
    }
    setSaving(true);
    try {
      if (dialog.mode === 'create') {
        await createAccountingCustomer(form);
        toast.success('Customer added.');
      } else {
        await updateAccountingCustomer(dialog.customer.ref, form);
        toast.success('Customer updated.');
      }
      setDialog(null);
      await load({ quiet: true });
    } catch (err) {
      toast.error(accountingError(err, 'The customer could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Export goes through fetch rather than a plain link because the API is
   * bearer-authenticated — a bare href would arrive unauthenticated and
   * bounce to the login page.
   */
  const exportCsv = async () => {
    setExporting(true);
    try {
      const response = await fetch(accountingCustomersExportUrl(), {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!response.ok) throw new Error('export failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'mcc-customers.csv';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('The customer export could not be downloaded.');
    } finally {
      setExporting(false);
    }
  };

  const canCreate = profile?.role === 'admin';
  const canDelete = profile?.is_super_admin === true;

  /**
   * Delete a manual customer.
   *
   * Only ever offered on a manual row. A student or applicant is a portal
   * record that Accounting reads, and the route refuses one outright — there
   * is no button for it, because a button that always fails is worse than no
   * button.
   */
  const confirmDelete = async () => {
    setBusyRef(deleteFor.ref);
    try {
      await deleteAccountingCustomer(deleteFor.ref);
      toast.success(`${deleteFor.name} deleted.`);
      setDeleteFor(null);
      await load({ quiet: true });
    } catch (err) {
      toast.error(accountingError(err, 'That customer could not be deleted.'));
    } finally {
      setBusyRef('');
    }
  };

  const actions = (
    <>
      <Button type="button" variant="outline" onClick={exportCsv} disabled={exporting}>
        {exporting ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Download className="mr-2 size-4" />}
        Export CSV
      </Button>
      <Button type="button" variant="outline" onClick={() => load({ quiet: true })} disabled={refreshing}>
        <RefreshCw className={cn('mr-2 size-4', refreshing && 'animate-spin')} />
        Refresh
      </Button>
      {canCreate && (
        <Button type="button" onClick={openCreate} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
          <Plus className="mr-2 size-4" />
          Add customer
        </Button>
      )}
    </>
  );

  return (
    <AccountingShell
      title="Customers"
      description="Every student and invoiced applicant is already a customer — there is nothing to convert. Add a record here only for someone the portal does not otherwise know."
      actions={actions}
    >
      <div className="flex flex-wrap items-center gap-2">
        {SOURCE_TABS.map((tab) => (
          <Button
            key={tab.id}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setFilter({ source: tab.id })}
            className={cn(filterPillClass, filters.source === tab.id && filterPillActiveClass)}
          >
            {tab.label}
            <span className="ml-1.5 opacity-70">({counts[tab.id] ?? 0})</span>
          </Button>
        ))}
        <div className="relative ml-auto w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#062B1A]/40" />
          <Input
            type="search"
            aria-label="Search customers"
            value={filters.q}
            onChange={(event) => setFilter({ q: event.target.value })}
            placeholder="Name, email, phone, account number"
            className={cn('pl-9', adminInputClass)}
          />
        </div>
        {filters.q && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setFilter({ q: '' })}>
            <X className="mr-1 size-3.5" />
            Clear
          </Button>
        )}
      </div>

      {canDelete && (
        <BulkDeleteBar
          count={selection.count}
          noun="customer"
          onDelete={() => setBulkOpen(true)}
          onClear={selection.clear}
        />
      )}

      {error ? (
        <AccountingError message={error} onRetry={load} />
      ) : loading ? (
        <TableSkeleton rows={6} />
      ) : rows.length === 0 ? (
        <AccountingEmpty
          title={filters.q ? 'No customers matching this search' : 'No customers yet'}
          description={
            filters.q
              ? 'Try a different name, email or account number.'
              : 'Students become customers as soon as they exist in the portal.'
          }
          action={
            filters.q ? (
              <Button type="button" variant="outline" onClick={() => setFilter({ q: '' })}>
                Clear search
              </Button>
            ) : null
          }
        />
      ) : (
        <Card className="overflow-hidden border border-[rgba(6,43,26,0.08)] shadow-sm">
          <div className="min-w-0 overflow-x-auto">
            <table className="w-full min-w-[970px] text-left text-sm">
              <thead className="bg-[#062B1A] text-[10px] uppercase tracking-[0.035em] text-white">
                <tr>
                  {canDelete && (
                    <th className="w-10 px-3 py-2.5">
                      <Checkbox
                        aria-label="Select every customer on this page that can be deleted"
                        className="border-white/50 data-[state=checked]:border-white data-[state=checked]:bg-white data-[state=checked]:text-[#062B1A]"
                        checked={selection.allSelected}
                        disabled={selection.selectableCount === 0}
                        onCheckedChange={selection.toggleAll}
                      />
                    </th>
                  )}
                  <th className="px-3 py-2.5 font-semibold">
                    <button type="button" onClick={() => toggleSort('name')} className="inline-flex items-center gap-1">
                      Name <ArrowUpDown className="size-3" />
                    </button>
                  </th>
                  <th className="px-3 py-2.5 font-semibold">
                    <button type="button" onClick={() => toggleSort('email')} className="inline-flex items-center gap-1">
                      Email <ArrowUpDown className="size-3" />
                    </button>
                  </th>
                  <th className="px-3 py-2.5 font-semibold">Phone</th>
                  <th className="px-3 py-2.5 font-semibold">Card</th>
                  <th className="px-3 py-2.5 text-right font-semibold">
                    <button type="button" onClick={() => toggleSort('balance')} className="inline-flex items-center gap-1">
                      Outstanding <ArrowUpDown className="size-3" />
                    </button>
                  </th>
                  <th className="px-3 py-2.5 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(6,43,26,0.07)]">
                {rows.map((customer) => (
                  <tr key={customer.ref} className="bg-white hover:bg-[#F7F8FA]/80">
                    {canDelete && (
                      <td className="px-3 py-3">
                        <Checkbox
                          aria-label={`Select ${customer.name || 'this customer'}`}
                          checked={selection.isSelected(customer)}
                          disabled={!customer.editable}
                          onCheckedChange={() => selection.toggle(customer)}
                        />
                      </td>
                    )}
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[#062B1A]">{customer.name || '—'}</span>
                        <StatusPill tone={SOURCE_TONES[customer.source_type] || 'slate'}>
                          {customer.source_label}
                        </StatusPill>
                      </div>
                      {customer.account_number && (
                        <div className="text-[11px] text-[#062B1A]/40">{customer.account_number}</div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs text-[#062B1A]/70">{customer.email || '—'}</td>
                    <td className="px-3 py-3 text-xs text-[#062B1A]/70">{customer.phone || '—'}</td>
                    <td className="px-3 py-3 text-xs text-[#062B1A]/35">
                      {/* No payment token is stored anywhere in the portal, so
                          there is nothing safe to show here. */}
                      {customer.card || 'Not on file'}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="text-sm font-semibold text-[#062B1A]">
                        {customer.outstanding_display}
                      </div>
                      {customer.overdue_cents > 0 && (
                        <div className="text-[11px] font-medium text-red-700">
                          Overdue: {customer.overdue_display}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button asChild variant="ghost" size="sm">
                          <Link
                            to={`/admin/accounting/sales/invoices?customer=${encodeURIComponent(customer.ref)}&tab=all`}
                          >
                            {customer.invoice_count} invoice{customer.invoice_count === 1 ? '' : 's'}
                          </Link>
                        </Button>
                        {customer.editable ? (
                          <Button type="button" variant="outline" size="sm" onClick={() => openEdit(customer)}>
                            <Pencil className="mr-1.5 size-3.5" />
                            Edit
                          </Button>
                        ) : customer.link ? (
                          <Button asChild variant="outline" size="sm">
                            <Link to={customer.link}>
                              <ExternalLink className="mr-1.5 size-3.5" />
                              Open record
                            </Link>
                          </Button>
                        ) : null}
                        {canDelete && customer.editable && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={busyRef === customer.ref}
                            className="text-red-700 hover:bg-red-50"
                            title={
                              customer.invoice_count > 0
                                ? 'This customer has invoices — remove those first'
                                : 'Delete this customer'
                            }
                            onClick={() => setDeleteFor(customer)}
                          >
                            <Trash2 className="size-3.5" />
                            <span className="sr-only">Delete</span>
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pageInfo && pageInfo.total_pages > 1 && (
            <div className="flex items-center justify-between border-t border-[rgba(6,43,26,0.07)] bg-white px-4 py-3 text-xs text-[#062B1A]/60">
              <span>{pageInfo.total_count} customers</span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pageInfo.current_page <= 1}
                  onClick={() => setFilters((current) => ({ ...current, page: String(pageInfo.current_page - 1) }))}
                >
                  Previous
                </Button>
                <span>
                  Page {pageInfo.current_page} of {pageInfo.total_pages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pageInfo.current_page >= pageInfo.total_pages}
                  onClick={() => setFilters((current) => ({ ...current, page: String(pageInfo.current_page + 1) }))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <Dialog open={Boolean(dialog)} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === 'edit' ? 'Edit customer' : 'Add customer'}</DialogTitle>
            <DialogDescription>
              For someone who is not a student or applicant. Students are already customers and are
              edited on their own record, so the portal keeps one source of truth for their details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="customer-name">Customer name</Label>
              <Input
                id="customer-name"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                placeholder="Business or person"
                className={cn('mt-1', adminInputClass)}
              />
            </div>

            <fieldset className="rounded-lg border border-[#062B1A]/10 p-3">
              <legend className="px-1 text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
                Primary contact
              </legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="customer-first">First name</Label>
                  <Input
                    id="customer-first"
                    value={form.first_name}
                    onChange={(event) => setForm({ ...form, first_name: event.target.value })}
                    className={cn('mt-1', adminInputClass)}
                  />
                </div>
                <div>
                  <Label htmlFor="customer-last">Last name</Label>
                  <Input
                    id="customer-last"
                    value={form.last_name}
                    onChange={(event) => setForm({ ...form, last_name: event.target.value })}
                    className={cn('mt-1', adminInputClass)}
                  />
                </div>
                <div>
                  <Label htmlFor="customer-email">Email</Label>
                  <Input
                    id="customer-email"
                    type="email"
                    value={form.email}
                    onChange={(event) => setForm({ ...form, email: event.target.value })}
                    className={cn('mt-1', adminInputClass)}
                  />
                </div>
                <div>
                  <Label htmlFor="customer-phone">Phone</Label>
                  <Input
                    id="customer-phone"
                    value={form.phone}
                    onChange={(event) => setForm({ ...form, phone: event.target.value })}
                    className={cn('mt-1', adminInputClass)}
                  />
                </div>
                <div>
                  <Label htmlFor="customer-phone-alt">Additional phone</Label>
                  <Input
                    id="customer-phone-alt"
                    value={form.phone_alt}
                    onChange={(event) => setForm({ ...form, phone_alt: event.target.value })}
                    className={cn('mt-1', adminInputClass)}
                  />
                </div>
              </div>
            </fieldset>

            <div className="space-y-3 rounded-lg border border-[#062B1A]/10 p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
                  Additional contacts
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setForm({
                      ...form,
                      additional_contacts: [
                        ...form.additional_contacts,
                        { first_name: '', last_name: '', email: '', phone: '' },
                      ],
                    })
                  }
                >
                  <Plus className="mr-1 size-3.5" />
                  Add contact
                </Button>
              </div>
              {form.additional_contacts.map((contact, index) => (
                <div key={index} className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
                  <Input
                    aria-label={`Contact ${index + 1} name`}
                    value={contact.first_name}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        additional_contacts: form.additional_contacts.map((row, position) =>
                          position === index ? { ...row, first_name: event.target.value } : row
                        ),
                      })
                    }
                    placeholder="First name"
                    className={adminInputClass}
                  />
                  <Input
                    aria-label={`Contact ${index + 1} email`}
                    value={contact.email}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        additional_contacts: form.additional_contacts.map((row, position) =>
                          position === index ? { ...row, email: event.target.value } : row
                        ),
                      })
                    }
                    placeholder="Email"
                    className={adminInputClass}
                  />
                  <Input
                    aria-label={`Contact ${index + 1} phone`}
                    value={contact.phone}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        additional_contacts: form.additional_contacts.map((row, position) =>
                          position === index ? { ...row, phone: event.target.value } : row
                        ),
                      })
                    }
                    placeholder="Phone"
                    className={adminInputClass}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove contact ${index + 1}`}
                    onClick={() =>
                      setForm({
                        ...form,
                        additional_contacts: form.additional_contacts.filter((_, position) => position !== index),
                      })
                    }
                  >
                    <X className="size-4 text-red-600" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="customer-account">Account number</Label>
                <Input
                  id="customer-account"
                  value={form.account_number}
                  onChange={(event) => setForm({ ...form, account_number: event.target.value })}
                  className={cn('mt-1', adminInputClass)}
                />
              </div>
              <div>
                <Label htmlFor="customer-website">Website</Label>
                <Input
                  id="customer-website"
                  value={form.website}
                  onChange={(event) => setForm({ ...form, website: event.target.value })}
                  placeholder="https://"
                  className={cn('mt-1', adminInputClass)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={saving}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              Save customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <BulkDeleteDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        noun="customer"
        rows={selection.selectedRows}
        labelOf={(row) => (row.email ? `${row.name} · ${row.email}` : row.name)}
        remove={(row) => deleteAccountingCustomer(row.ref)}
        onFinished={() => {
          selection.clear();
          load({ quiet: true });
        }}
      />

      <ConfirmDeleteDialog
        open={Boolean(deleteFor)}
        onOpenChange={(next) => !next && setDeleteFor(null)}
        title="Delete this customer?"
        detail={deleteFor ? `${deleteFor.name}${deleteFor.email ? ` · ${deleteFor.email}` : ''}` : ''}
        consequence={
          deleteFor?.invoice_count > 0
            ? 'This customer has invoices. The server will refuse the delete so those invoices '
              + 'are not left addressed to nobody.'
            : ''
        }
        confirmLabel="Delete customer"
        busy={Boolean(busyRef)}
        onConfirm={confirmDelete}
      />
    </AccountingShell>
  );
}
